import * as THREE from 'three';

const START_Y = 42;
const GROUND_SNAP_THRESHOLD_Y = 1.1;
const BOX_SIZE = 0.7;
const FALL_SPEED = 14;
const INTERACT_RANGE = 3.5;
const LOOK_DOT_MIN = 0.86;

const BEACON_RADIUS = 0.06;
const BEACON_HEIGHT = 11;
const BEAM_GOLD = 0xf1c40f;
const BEACON_OPACITY_BASE = 0.22;
const BEACON_PULSE_SPEED = 2.4;
const BEACON_PULSE_AMP = 0.08;

export class FpsSupplyDrop {
  private readonly group: THREE.Group;
  private readonly mesh: THREE.Mesh;
  private readonly geometry: THREE.BoxGeometry;
  private readonly material: THREE.MeshStandardMaterial;
  private landed = false;
  private opened = false;
  /** Crate stays aloft until the supply plane passes the drop XZ. */
  private dropReleased = false;
  private beaconMesh: THREE.Mesh | null = null;
  private beaconGeometry: THREE.CylinderGeometry | null = null;
  private beaconMaterial: THREE.MeshBasicMaterial | null = null;
  private smokeRing: THREE.Mesh | null = null;
  private smokeRingMat: THREE.MeshBasicMaterial | null = null;
  private smokeRingLife = 0;

  constructor(sceneRoot: THREE.Group, landX: number, landZ: number) {
    this.geometry = new THREE.BoxGeometry(BOX_SIZE, BOX_SIZE, BOX_SIZE);
    this.material = new THREE.MeshStandardMaterial({
      color: 0x7a3cff,
      emissive: 0x4b1fb8,
      emissiveIntensity: 0.85,
      metalness: 0.2,
      roughness: 0.35,
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;

    this.group = new THREE.Group();
    this.group.position.set(landX, START_Y, landZ);
    this.group.add(this.mesh);
    sceneRoot.add(this.group);
  }

  update(dt: number): void {
    if (this.opened) return;

    if (!this.landed) {
      if (!this.dropReleased) return;
      if (!Number.isFinite(dt) || dt <= 0) return;

      const y = this.group.position.y;
      const nextY = y - FALL_SPEED * dt;
      const groundCenterY = BOX_SIZE * 0.5;
      if (nextY <= GROUND_SNAP_THRESHOLD_Y) {
        this.group.position.y = groundCenterY;
        this.landed = true;
        this.addLandingBeacon();
        this.spawnSmokeRing();
      } else {
        this.group.position.y = nextY;
      }
      return;
    }

    this.pulseBeacon();
    if (this.smokeRing && this.smokeRingLife > 0) {
      this.smokeRingLife -= dt;
      const t = Math.max(0, this.smokeRingLife / 1.4);
      if (this.smokeRingMat) {
        this.smokeRingMat.opacity = t * 0.55;
      }
      const scale = 1 + (1 - t) * 2.2;
      this.smokeRing.scale.set(scale, 1, scale);
      if (this.smokeRingLife <= 0) {
        this.group.remove(this.smokeRing);
        this.smokeRing.geometry.dispose();
        this.smokeRingMat?.dispose();
        this.smokeRing = null;
        this.smokeRingMat = null;
      }
    }
  }

  private spawnSmokeRing(): void {
    if (this.smokeRing) return;
    const geo = new THREE.RingGeometry(0.35, 1.05, 32);
    this.smokeRingMat = new THREE.MeshBasicMaterial({
      color: 0x8899aa,
      transparent: true,
      opacity: 0.55,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    this.smokeRing = new THREE.Mesh(geo, this.smokeRingMat);
    this.smokeRing.rotation.x = -Math.PI / 2;
    this.smokeRing.position.y = 0.06;
    this.group.add(this.smokeRing);
    this.smokeRingLife = 1.4;
  }

  private addLandingBeacon(): void {
    if (this.beaconMesh) return;

    this.beaconGeometry = new THREE.CylinderGeometry(BEACON_RADIUS, BEACON_RADIUS, BEACON_HEIGHT, 16);
    this.beaconMaterial = new THREE.MeshBasicMaterial({
      color: BEAM_GOLD,
      transparent: true,
      opacity: BEACON_OPACITY_BASE,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    this.beaconMesh = new THREE.Mesh(this.beaconGeometry, this.beaconMaterial);
    const halfBox = BOX_SIZE * 0.5;
    const halfH = BEACON_HEIGHT * 0.5;
    this.beaconMesh.position.set(0, halfH - halfBox, 0);
    this.group.add(this.beaconMesh);
  }

  private pulseBeacon(): void {
    if (!this.beaconMaterial) return;
    const t = performance.now() * 0.001;
    this.beaconMaterial.opacity =
      BEACON_OPACITY_BASE + Math.sin(t * BEACON_PULSE_SPEED) * BEACON_PULSE_AMP;
  }

  releaseDrop(): void {
    this.dropReleased = true;
  }

  isDropReleased(): boolean {
    return this.dropReleased;
  }

  isLanded(): boolean {
    return this.landed;
  }

  isFalling(): boolean {
    return this.dropReleased && !this.landed && !this.opened;
  }

  getWorldPosition(out: THREE.Vector3): THREE.Vector3 {
    this.group.getWorldPosition(out);
    return out;
  }

  shouldShowPrompt(playerPos: THREE.Vector3, cameraDir: THREE.Vector3): boolean {
    if (!this.landed || this.opened) return false;
    const center = this.group.position.clone();
    const toBox = center.sub(playerPos);
    const dist = toBox.length();
    if (dist >= INTERACT_RANGE || dist < 1e-5) return false;
    const dir = toBox.multiplyScalar(1 / dist);
    const look = cameraDir.clone().normalize();
    return look.dot(dir) > LOOK_DOT_MIN;
  }

  tryOpen(playerPos: THREE.Vector3, cameraDir: THREE.Vector3): boolean {
    if (!this.landed || this.opened) return false;

    const center = this.group.position.clone();
    const toBox = center.sub(playerPos);
    const dist = toBox.length();
    if (dist >= INTERACT_RANGE || dist < 1e-5) return false;

    const dir = toBox.multiplyScalar(1 / dist);
    const look = cameraDir.clone().normalize();
    if (look.dot(dir) <= LOOK_DOT_MIN) return false;

    this.opened = true;
    this.mesh.visible = false;
    if (this.beaconMesh) this.beaconMesh.visible = false;
    return true;
  }

  getOpened(): boolean {
    return this.opened;
  }

  /** Bot contest — marks crate looted without player interaction. */
  forceOpenByBot(): void {
    if (this.opened) return;
    this.opened = true;
    this.mesh.visible = false;
    if (this.beaconMesh) this.beaconMesh.visible = false;
  }

  getXZ(): { x: number; z: number } {
    return { x: this.group.position.x, z: this.group.position.z };
  }

  dispose(): void {
    if (this.beaconMesh) {
      this.group.remove(this.beaconMesh);
      this.beaconMesh = null;
    }
    if (this.beaconGeometry) {
      this.beaconGeometry.dispose();
      this.beaconGeometry = null;
    }
    if (this.beaconMaterial) {
      this.beaconMaterial.dispose();
      this.beaconMaterial = null;
    }
    if (this.smokeRing) {
      this.group.remove(this.smokeRing);
      this.smokeRing.geometry.dispose();
      this.smokeRing = null;
    }
    if (this.smokeRingMat) {
      this.smokeRingMat.dispose();
      this.smokeRingMat = null;
    }
    this.group.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
  }
}
